/**
 * The file connector's API, typed at the edge of this package.
 *
 * Its own package rather than a panel inside `feature-integration`, mirroring
 * the backend exactly: the module and this package are removed together, and
 * neither the contract's screens nor this one imports the other. A feature may
 * not import a sibling, and that rule is doing real work here — it is what
 * stops the contract's UI from growing a branch per connector.
 */

import type { ApiClient } from "@hardauto/core-api";

const BASE = "/api/v1/connector/files";

export type FileFormat = "csv" | "xlsx";

export interface ImportReport {
  entity: string;
  filename: string;
  file_format: FileFormat;
  /**
   * What the reader assumed — the delimiter and the encoding.
   *
   * Shown on screen, because "0 lignes importées" with nothing else is the
   * worst possible answer: a French Excel writes `;` and an English one `,`,
   * and an operator cannot tell whether the file or the mapping is wrong
   * without this.
   */
  dialect: string;
  sheet: string;
  header: string[];
  received: number;
  created: number;
  updated: number;
  unchanged: number;
  refused: number;
  failed: number;
  anomalies: number;
  /** Nothing was written. Two harmless causes, two different sentences. */
  wrote_nothing: boolean;
  unmapped: string[];
  examples: string[];
}

export type ImportStatus = "queued" | "running" | "done" | "failed";

/**
 * One uploaded file, imported in the background.
 *
 * The upload answers as soon as the file is read and stored; a worker then
 * puts the rows through the contract slice by slice. `processed` against
 * `total` is the progress, and `report`'s counts are final once `status` is
 * `done`.
 */
export interface ImportRun {
  id: string;
  status: ImportStatus;
  total: number;
  processed: number;
  /** Why the run could not finish. Empty unless `status` is `failed`. */
  error: string;
  created_at: string | null;
  finished_at: string | null;
  report: ImportReport;
}

export function isFinished(run: ImportRun): boolean {
  return run.status === "done" || run.status === "failed";
}

/**
 * How a column is compared with the filter's value. Mirrors the server's `Operator`.
 * `in_list` takes several values separated by `;`; the two `empty` operators take none.
 */
export type ExportOperator =
  | "contains"
  | "not_contains"
  | "equals"
  | "not_equals"
  | "starts_with"
  | "ends_with"
  | "in_list"
  | "is_empty"
  | "not_empty";

export const EXPORT_OPERATORS: readonly ExportOperator[] = [
  "contains",
  "not_contains",
  "equals",
  "not_equals",
  "starts_with",
  "ends_with",
  "in_list",
  "is_empty",
  "not_empty",
];

/** The two operators that compare with nothing: no value is typed or sent. */
export function operatorTakesValue(operator: ExportOperator): boolean {
  return operator !== "is_empty" && operator !== "not_empty";
}

/**
 * One condition on one column. Every filter must hold for a row to be exported: there is
 * no hidden "either" (use `in_list` for alternatives).
 */
export interface ExportFilter {
  column: string;
  operator: ExportOperator;
  text: string;
}

/**
 * The wire form `column|operator|value`. A filter that needs a value and has none is
 * dropped (a half-typed row must not turn into "export everything that contains nothing").
 */
export function exportFilterParam(filter: ExportFilter): string | null {
  if (!filter.column) return null;
  if (!operatorTakesValue(filter.operator)) return `${filter.column}|${filter.operator}|`;
  if (!filter.text.trim()) return null;
  return `${filter.column}|${filter.operator}|${filter.text}`;
}

export interface SpooledFile {
  name: string;
  size: number;
  written_at: string;
}

export interface SpoolList {
  items: SpooledFile[];
  /**
   * False on a backend this route cannot list — object storage, read by the
   * installation's own tooling. The screen then says where the files are
   * instead of claiming there are none.
   */
  is_listable: boolean;
}

/** Every file-connector call, in one place. */
export class ConnectorFilesApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  /** Entities a file may be imported into. */
  importable(): Promise<{ items: string[] }> {
    return this.#client.request<{ items: string[] }>(`${BASE}/entities`);
  }

  /**
   * Upload one file into one entity. Answers with the queued run, not the
   * result: follow it with `importRun`.
   *
   * Through the client's `upload`, which sends `FormData` and deliberately
   * does NOT set `Content-Type` — the browser generates the multipart
   * boundary. Passing the file as a JSON `body` would serialise it as `{}`,
   * which arrives as an empty upload and reports zero rows with the file
   * sitting right there on the operator's screen.
   */
  importFile(entity: string, file: File, sheet?: string): Promise<ImportRun> {
    const form = new FormData();
    form.append("upload", file, file.name);
    const query = sheet ? `?sheet=${encodeURIComponent(sheet)}` : "";
    return this.#client.upload<ImportRun>(
      `${BASE}/import/${encodeURIComponent(entity)}${query}`,
      form,
    );
  }

  /** One import, as far as it has gone. */
  importRun(id: string): Promise<ImportRun> {
    return this.#client.request<ImportRun>(`${BASE}/imports/${encodeURIComponent(id)}`);
  }

  /** The latest imports, newest first. */
  recentImports(limit = 10): Promise<{ items: ImportRun[] }> {
    return this.#client.request<{ items: ImportRun[] }>(`${BASE}/imports?limit=${limit}`);
  }

  /** The export's URL, for a link the browser downloads. */
  exportUrl(entity: string, delimiter: "semicolon" | "comma" | "tab" = "semicolon"): string {
    return `${BASE}/export/${encodeURIComponent(entity)}?delimiter=${delimiter}`;
  }

  /**
   * The export as a blob, for a download the page triggers itself.
   *
   * `requestBlob` rather than a plain `<a href>`: the export is behind a
   * bearer token, and a link the browser follows carries no Authorization
   * header — it would land on the sign-in page and download the HTML.
   */
  exportFile(
    entity: string,
    delimiter: "semicolon" | "comma" | "tab" = "semicolon",
    options: { fields?: readonly string[]; where?: readonly ExportFilter[] } = {},
  ): Promise<Blob> {
    const extra = [
      ...(options.fields ?? []).map((name) => `&field=${encodeURIComponent(name)}`),
      ...(options.where ?? [])
        .map(exportFilterParam)
        .filter((param): param is string => param !== null)
        .map((param) => `&where=${encodeURIComponent(param)}`),
    ];
    return this.#client.requestBlob(this.exportUrl(entity, delimiter) + extra.join(""));
  }

  /** The columns an export of this entity can carry. */
  exportFields(entity: string): Promise<{ fields: string[] }> {
    return this.#client.request<{ fields: string[] }>(
      `${BASE}/export/${encodeURIComponent(entity)}/fields`,
    );
  }

  spool(limit = 50): Promise<SpoolList> {
    return this.#client.request<SpoolList>(`${BASE}/spool?limit=${limit}`);
  }
}

/** One table a reset empties, and its rows. */
export interface TableCount {
  table: string;
  rows: number;
}

/** What a data reset removes — before it runs — or removed, after. */
export interface DataResetSummary {
  /** What the operator must type back to confirm. */
  tenant_slug: string;
  tenant_name: string;
  /** Non-empty tables only. */
  tables: TableCount[];
  total_rows: number;
  /** Portal accounts removed with the customers they act for. */
  customer_accounts: number;
  /** Back-office accounts, which the reset keeps. */
  kept_accounts: number;
}

/**
 * The data reset, which the `maintenance` module serves.
 *
 * Here rather than in a package of its own because the panel lives on this
 * screen: emptying the tenant is the step before re-importing an extract.
 * Nothing in this package depends on it existing — the backend answers 404
 * when the deployment has not enabled it, and the panel then renders nothing.
 */
export class DataResetApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  preview(): Promise<DataResetSummary> {
    return this.#client.request<DataResetSummary>("/api/v1/maintenance/data-reset");
  }

  reset(confirmation: string): Promise<DataResetSummary> {
    return this.#client.request<DataResetSummary>("/api/v1/maintenance/data-reset", {
      method: "POST",
      body: { confirmation },
    });
  }
}
